/**
 * A position's rate, rendered safely.
 *
 * `Position::$casts` types `rate` as an ARRAY, and the consultant paths do use
 * the array form — `Position::calculateStaffMonthlyRate()` reads `rate['price']`
 * and `rate['amount']` as a per-unit price and a unit count, multiplying them
 * for the total. Handing React an object throws "Objects are not valid as a
 * React child", and React 16 without an error boundary unmounts the WHOLE root:
 * the symptom is a blank page, not a stack trace anyone reports.
 *
 * That the shipped position-fund screens render it bare is not evidence that it
 * is always scalar. All of them are reached by the same rare persona, so they
 * share one blind spot rather than confirming the shape three times over — and
 * what keeps it scalar today is two unrelated fatals upstream, not a guarantee
 * about the column.
 *
 * `typeof null === 'object'`, so a null rate takes the object branch and the
 * optional chain returns undefined — React renders nothing, which is the right
 * answer for a rate that is not set.
 *
 * `price` and `amount` are multiplicands, not alternatives — a secondee/
 * volunteer rate carries `price` alone, but a consultant-shaped rate with both
 * keys set means "price per unit, this many units", so `price` on its own is
 * the wrong figure for that shape and `amount` alone would be worse (a unit
 * count rendered as if it were money). Multiplying when both are present reads
 * the total the same way `calculateStaffMonthlyRate()` does.
 */
export const positionFundRate = (amountData) => {
    const rate = amountData?.rate;

    if (typeof rate !== 'object' || rate === null) {
        return rate;
    }

    return rate.amount != null ? rate.price * rate.amount : rate.price;
};
